import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Calendar,
  User,
  Eye,
  Clock,
  Loader2,
  Share2,
  Bookmark,
  ChevronRight,
  Building2,
  BookOpen,
  ExternalLink,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Check,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { articlesAPI } from '@/services/api';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await articlesAPI.getById(slug);
      setArticle(response.data);
      
      // Fetch related articles based on industries
      if (response.data.industry_ids?.length > 0) {
        const relatedRes = await articlesAPI.getAll({
          industry_id: response.data.industry_ids[0],
          limit: 4,
        });
        // Filter out current article
        const related = (relatedRes.data?.items || []).filter(a => a.id !== response.data.id);
        setRelatedArticles(related.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Article not found');
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'MMMM dd, yyyy');
  };

  const estimateReadTime = (content) => {
    if (!content) return 5;
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = async (platform) => {
    const title = article?.title || 'Check out this article';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="container-custom py-20 text-center">
          <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h1 className="font-heading text-2xl text-navy-700 mb-4">Article not found</h1>
          <Link to="/articles">
            <Button variant="outline" className="rounded-xl">Back to Articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="article-detail-page">
      {/* Hero Image */}
      {article.featured_image && (
        <div className="w-full h-64 md:h-96 relative">
          <img
            src={article.featured_image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-zinc-900/40" />
        </div>
      )}

      {/* Article Content */}
      <article className="container-custom py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/articles" className="inline-flex items-center text-slate-600 hover:text-navy-700 mb-6 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Articles
          </Link>

          {/* Article Header */}
          <header className="mb-8">
            {/* Categories/Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {article.industries?.map(ind => (
                <Link key={ind.id} to={`/articles?industry=${ind.id}`}>
                  <Badge className="bg-navy-100 text-navy-700 hover:bg-navy-200 cursor-pointer">
                    <Building2 className="w-3 h-3 mr-1" />
                    {ind.name}
                  </Badge>
                </Link>
              ))}
              {(article.tags || []).map(tag => (
                <Link key={tag} to={`/articles?tag=${tag}`}>
                  <Badge variant="secondary" className="hover:bg-slate-200 cursor-pointer">
                    {tag}
                  </Badge>
                </Link>
              ))}
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-navy-700 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 pb-6 border-b border-slate-200">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(article.published_at)}
              </span>
              {article.author_name && (
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  {article.author_name}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {article.view_count} views
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {estimateReadTime(article.content)} min read
              </span>

              {/* Share Buttons */}
              <div className="ml-auto flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-lg border-slate-300">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleShare('facebook')}>
                      <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                      Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('twitter')}>
                      <Twitter className="w-4 h-4 mr-2 text-sky-500" />
                      Twitter
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('linkedin')}>
                      <Linkedin className="w-4 h-4 mr-2 text-blue-700" />
                      LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('copy')}>
                      {copied ? (
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy Link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Excerpt/Summary */}
          {article.excerpt && (
            <div className="mb-8 p-6 bg-slate-50 rounded-xl border-l-4 border-gold-500">
              <p className="text-lg text-slate-700 italic leading-relaxed">
                {article.excerpt}
              </p>
            </div>
          )}

          {/* Article Body */}
          <div className="prose prose-lg prose-slate max-w-none mb-12">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="font-heading text-3xl font-bold text-navy-700 mt-10 mb-4">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="font-heading text-2xl font-bold text-navy-700 mt-8 mb-3">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="font-heading text-xl font-bold text-navy-700 mt-6 mb-2">{children}</h3>
                ),
                p: ({ children }) => (
                  <p className="text-slate-600 leading-relaxed mb-4">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-slate-600">{children}</li>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-gold-500 pl-4 my-6 italic text-slate-600">
                    {children}
                  </blockquote>
                ),
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-navy-600 hover:text-gold-600 underline"
                  >
                    {children}
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                ),
                code: ({ children }) => (
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono text-navy-700">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto mb-4">
                    {children}
                  </pre>
                ),
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {/* Author Box (if author exists) */}
          {article.author_name && (
            <Card className="border-slate-200 mb-12">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-navy-700 flex items-center justify-center text-white text-xl font-bold">
                    {article.author_name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Written by</p>
                    <h4 className="font-heading text-lg font-bold text-navy-700">
                      {article.author_name}
                    </h4>
                    <p className="text-slate-600 text-sm">GRC Industry Expert</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTA Section */}
          <Card className="border-0 shadow-xl bg-[#09090b] text-white mb-12">
            <CardContent className="p-8 text-center">
              <h3 className="font-heading text-2xl font-bold mb-3">
                Want to Learn More?
              </h3>
              <p className="text-slate-300 mb-6 max-w-lg mx-auto">
                Explore our comprehensive webinars and seminars on Governance, Risk, and Compliance topics.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/webinars">
                  <Button className="bg-gold-500 hover:bg-gold-600 text-navy-800 rounded-xl font-semibold">
                    Browse Webinars
                  </Button>
                </Link>
                <Link to="/seminars">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-xl">
                    View Seminars
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-slate-50 py-12">
          <div className="container-custom">
            <h2 className="font-heading text-2xl font-bold text-navy-700 mb-8">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link key={related.id} to={`/articles/${related.slug}`}>
                  <Card className="group border-slate-200 hover:border-gold-400 hover:shadow-lg transition-all h-full">
                    <div className="h-40 overflow-hidden">
                      <img
                        src={related.thumbnail_url || related.featured_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80'}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-xs text-slate-500 mb-2">
                        {formatDate(related.published_at)}
                      </p>
                      <h3 className="font-heading text-lg font-semibold text-navy-700 group-hover:text-gold-600 transition-colors line-clamp-2 mb-2">
                        {related.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {related.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <Link to="/articles">
                <Button variant="outline" className="rounded-xl border-slate-300">
                  View All Articles
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
